import { extractPdfAttachments } from "@/lib/file-operations/pdf-tools";
import { useTenant } from "@/providers/session";
import { useNavigate } from "@tanstack/react-router";
import { PDFDocument } from "pdf-lib";
import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { create } from "zustand";

interface DocumentSplitDropzoneProps {
  children?: (props: { error: string | null }) => React.ReactNode;
  clickUploadEnabled?: boolean;
  maxTotalSizeMB?: number;
}

export const DocumentSplitDropzone: React.FC<DocumentSplitDropzoneProps> = ({
  children,
  clickUploadEnabled = false,
  maxTotalSizeMB = 200,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const { companySlug } = useTenant();
  const { setPdfFiles } = usePendingSplitStore();
  const [error, setError] = useState<string | null>(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadError = (error: any): void => {
    const errorCode = error?.data?.code;
    const errorData = error?.data?.data;

    const errMessage = errorCode
      ? (t(`server_errors.${errorCode}`, { ...errorData }) as string)
      : error.message || t("generic_error");

    setError(errMessage);

    toast.error(errMessage, { richColors: true });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  const handleFileUpload = async (files: FileList) => {
    const validFiles: File[] = [];
    const maxTotalSizeBytes = maxTotalSizeMB * 1024 * 1024;
    let totalSize = 0;

    for (const file of Array.from(files)) {
      const validTypes = [".pdf"];
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;

      if (!validTypes.includes(fileExtension)) {
        handleUploadError(
          new Error(
            `Nicht unterstützter Dateityp: ${fileExtension}. Für die Seitenbearbeitung werden nur PDF-Dateien unterstützt.`,
          ),
        );
        continue;
      }

      const fileBuffer = await file.arrayBuffer();

      const pdfDoc = await PDFDocument.load(fileBuffer, {
        ignoreEncryption: true,
      });

      const attachment = await extractPdfAttachments(pdfDoc);

      if (attachment.length > 0) {
        handleUploadError(new Error(t("documents_organize_e-invoices-error")));
        return;
      }

      totalSize += file.size;
      validFiles.push(file);
    }

    if (totalSize > maxTotalSizeBytes) {
      handleUploadError(
        new Error(
          `Die gesamte Upload-Größe (${formatFileSize(
            totalSize,
          )}) überschreitet das maximale Limit von ${maxTotalSizeMB} MB.`,
        ),
      );
      return;
    }

    if (validFiles.length === 0) {
      return;
    }

    try {
      setPdfFiles(validFiles);

      navigate({
        to: "/$company/buchhaltung/rechnung/seiten-bearbeiten",
        params: { company: companySlug },
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      handleUploadError(error);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files?.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  return (
    <>
      <div
        className="relative"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {clickUploadEnabled ? (
          <div onClick={handleUploadClick} className="cursor-pointer">
            {children?.({ error })}
          </div>
        ) : (
          <div>{children?.({ error })}</div>
        )}
        <input
          type="file"
          accept=".pdf,application/pdf"
          hidden
          multiple
          ref={fileInputRef}
          onChange={handleInputChange}
        />
      </div>
    </>
  );
};

type PendingSplitState = {
  pdfFiles: File[];
  setPdfFiles: (files: File[]) => void;
  clearFiles: () => void;
};

export const usePendingSplitStore = create<PendingSplitState>((set) => ({
  pdfFiles: [],
  setPdfFiles: (files) => set({ pdfFiles: files }),
  clearFiles: () => set({ pdfFiles: [] }),
}));
